// @ts-check

import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import didUpdate from "@ember/render-modifiers/modifiers/did-update";
import dCategoryLink from "discourse/ui-kit/helpers/d-category-link";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import dScrollIntoView from "discourse/ui-kit/modifiers/d-scroll-into-view";

/**
 * @typedef {import("discourse/lib/types/d-autocomplete").HashtagAutocompleteResult} HashtagAutocompleteResult
 */

/**
 * Component for rendering hashtag autocomplete results for the DAutocomplete modifier.
 *
 * This component handles rendering of categories and tags in the autocomplete
 * dropdown, and is designed to be used with DAutocomplete's `component` API.
 *
 * @component HashtagAutocompleteResults
 * @implements {Component<import("discourse/lib/types/d-autocomplete").AutocompleteResultsSignature<HashtagAutocompleteResult>>}
 */
export default class HashtagAutocompleteResults extends Component {
  static TRIGGER_KEY = "#";

  @tracked isInitialRender = true;

  /**
   * @param {HashtagAutocompleteResult} result
   * @param {number} index
   * @param {Event} event
   */
  @action
  handleResultClick(result, index, event) {
    event.preventDefault();
    event.stopPropagation();
    this.args.onSelect(result, index, event);
  }

  @action
  handleInsert() {
    this.args.onRender?.(this.args.results);
  }

  @action
  handleUpdate() {
    this.isInitialRender = false;
    this.args.onRender?.(this.args.results);
  }

  /** @param {number} index */
  @action
  shouldScroll(index) {
    return index === this.args.selectedIndex && !this.isInitialRender;
  }

  /** @param {HashtagAutocompleteResult} result */
  @action
  getResultLabel(result) {
    if (result.model) {
      return dCategoryLink(result.model, {
        allowUncategorized: true,
        link: false,
      });
    }

    return `${result.name} x ${result.count}`;
  }

  <template>
    <div
      class="autocomplete ac-category-or-tag"
      {{didInsert this.handleInsert}}
      {{didUpdate this.handleUpdate @selectedIndex}}
    >
      <ul>
        {{#each @results as |result index|}}
          <li {{dScrollIntoView (this.shouldScroll index)}}>
            <a
              class={{if (eq index @selectedIndex) "selected"}}
              href
              {{on "click" (fn this.handleResultClick result index)}}
            >
              {{#unless result.model}}
                {{dIcon "tag"}}
              {{/unless}}
              <span class="text-content">{{this.getResultLabel result}}</span>
            </a>
          </li>
        {{/each}}
      </ul>
    </div>
  </template>
}
